import React, { useState, useContext, useRef, useMemo, useEffect } from "react";
import NVHorizontalBarChart, {
  formats,
} from "common/graphs/NVHorizontalBarChart";
import styled from "styled-components";
import BqnContext from "common/BqnContext";

const WrapperBqn = styled.div`
  .centered {
    display: flex;
    justify-content: center;
  }
`;

const ControlsWrapper = styled.div`
  margin: 10px 40px 50px 70px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const ControlItemWrapper = styled.div`
  display: inline-flex;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 0px 0 0px;
  gap: 5px;
  align-items: center;
  cursor: pointer;
  width: 15ch;

  .square {
    width: 12px;
    min-width: 12px;
    height: 12px;
    border: 2px solid;
    border-radius: 6px;
  }

  label {
    user-select: none;
    margin-bottom: 0;
    font-size: 13px;
    font-weight: normal;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// Left margin fits interface labels: device name plus the "-access"
// suffix at the ~8px per character of the axis font.
const DEVICE_NAME_CHARS = 12;
const ROLE_SUFFIX_CHARS = "-access".length;
const CHAR_WIDTH_PX = 8;
const CHART_MARGIN = {
  top: 0,
  right: 50,
  bottom: 10,
  left: (DEVICE_NAME_CHARS + ROLE_SUFFIX_CHARS) * CHAR_WIDTH_PX,
};

const CONTROL_ITEMS = [
  {
    label: "Grouped",
    key: "Grouped",
    color: "#444444",
    field: "grouped",
  },
  {
    label: "Stacked",
    key: "Stacked",
    color: "#444444",
    field: "stacked",
  },
];

const getAspects = () => [
  {
    label: "Tx",
    color: "#1f77b4",
    field: "tx",
  },
  {
    label: "Rx",
    color: "#ffa500",
    field: "rx",
  },
];

const throughputByAspects = (usage = [], aspects) => {
  return aspects.map(({ label, color, field }) => {
    const values = usage.reduce((acc, value)=>{
      const {name, bqn} = value;
      if (name === "access" && bqn !== "all"){
        acc.push({label:"", value: new Number(undefined)})
      }
      acc.push({label: name, value: Number(value[field])})
     return acc;
    },[])
    return ({
    key: label,
    label,
    color,
    values,
  })});
}

const getMaxValue = (usage, stacked) => {
  return Object.values(usage).reduce((acc, item) => {
    const maxValues = item.reduce((accItem, { rx = 0, tx = 0 }) => {
      const val = stacked ? rx + tx : Math.max(rx, tx);
      return Math.max(val, accItem);
    }, 0);
    return Math.max(maxValues, acc);
  }, 0);
};

const ClusterUsage = ({ usagePerBqn, stacked, setStacked, fixedYRange }) => {
  const aspects = getAspects();
  const [aspectsSelection, setAspectsSelection] = useState(
    new Set(aspects.map((aspect) => aspect.field))
  );

  const handleGroupedStacked = (clicked) => {
    setStacked(clicked === "stacked");
  };

  const handleAspectSelection = (aspect) => {
    const nextSelection = new Set(aspectsSelection);
    if (nextSelection.has(aspect)) {
      nextSelection.delete(aspect);
    } else {
      nextSelection.add(aspect);
    }
    setAspectsSelection(nextSelection);
  };

  return (
    <>
      <ControlsWrapper>
        {CONTROL_ITEMS.map(({ label, key, color, field }) =>
            <ControlItem
              label={label}
              key={key}
              color={color}
              isSelected={field === "grouped" ? stacked === false : stacked === true}
              field={field}
              onSelection={handleGroupedStacked}
            />
        )}
        {aspects.map(({ label, color, field }) => (
            <ControlItem
              label={label}
              color={color}
              key={label}
              field={field}
              isSelected={aspectsSelection.has(field)}
              onSelection={handleAspectSelection}
            />
          )
        )}
      </ControlsWrapper>
      {Object.entries(usagePerBqn).map(([bqn, usage]) => {
        const selectedAspects = aspects.filter(({ field }) =>
          aspectsSelection.has(field)
        );
        const key = `thr-${bqn}-st${stacked === true ? "on" : "off"}${[...aspectsSelection].join('-')}`;
        return (
          <WrapperBqn key={`througput-${bqn}`}>
            <h6>BQN {bqn.toUpperCase()}</h6>
            <div>
              <NVHorizontalBarChart
                tooltipValueFormatter={(value) =>
                  `${formats.twoDecimals(value)} Mbps`
                }
                valueFormat={formats.twoDecimals}
                stacked={stacked}
                items={throughputByAspects(usage, selectedAspects)}
                key={key}
                id={key}
                showRoundCorners={false}
                animationDuration={750}
                showColors={false}
                valuePadding={30}
                hideLegends={true}
                margin={CHART_MARGIN}
                fixedYRange={fixedYRange}
                showValues={true}
                showStackedValues={stacked}
              />
              <div className="centered">Mbps</div>
            </div>
          </WrapperBqn>
        );
      })}
    </>
  );
};

const ControlItem = ({ label, isSelected, onSelection, color, field }) => {
  const handleClick = () => {
    onSelection(field);
  };
  return (
    <ControlItemWrapper onClick={handleClick}>
      <div
        className="square"
        style={{
          backgroundColor: isSelected ? color : "transparent",
          borderColor: color,
        }}
      ></div>
      <label title={label}>{label}</label>
    </ControlItemWrapper>
  );
};

const GroupedOrStacked = ({ items, bqn, barHeight, stacked, setStacked, ...rest }) => {
  const doSetStacked = () => setStacked(true);
  const doSetGrouped = () => setStacked(false);
  const controls = CONTROL_ITEMS.map(({ label, key, color, field }) => {
    return {
      label,
      key,
      color,
      field,
      doToggle: field === "grouped" ? doSetGrouped : doSetStacked,
      disabled: field === "grouped" ? stacked === true : stacked === false,
    };
  });

  return (
    <NVHorizontalBarChart
      barHeight={barHeight}
      prependLegends={controls}
      stacked={stacked}
      items={items}
      animationDuration={750}
      showValues={true}
      showStackedValues={stacked}
      key={`througput-${bqn}-stacked${stacked === true ? "on" : "off"}`}
      {...rest}
    />
  );
};

const getFilteredUsage = ({ usage, bqnId }) => {
  if (bqnId === "all") {
    return usage;
  }
  return usage.filter(({ bqn }) => {
    return parseFloat(bqn) === parseFloat(bqnId);
  });
};

const Result = ({ usage = [] }) => {
  const {
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);

  const [stacked, setStacked] = useState(false);
  const [yRange, setYRange] = useState(null);
  
  const countRef = useRef(0);
  const rangeMaxRef = useRef(0);
  const prevBqnId = useRef(bqnId);
  const prevMode = useRef(mode);

  const filteredUsage = useMemo(
    () => getFilteredUsage({ usage, bqnId }),
    [usage, bqnId],
  );
  
  const usagePerBqn = useMemo(() => {
    return filteredUsage.reduce((acc, item) => {
      const { bqn } = item;
      acc[bqn] = acc[bqn] ? [...acc[bqn], item] : [item];
      return acc;
    }, {});
  }, [filteredUsage]);

  const maxValue = getMaxValue(usagePerBqn, stacked);

  useEffect(() => {
    if (prevBqnId.current !== bqnId || prevMode.current !== mode) {
      prevBqnId.current = bqnId;
      prevMode.current = mode;
      rangeMaxRef.current = 0;
      countRef.current = 0;
      setYRange(null);
      return;
    }

    const safeMaxValue = isNaN(maxValue) || !isFinite(maxValue) ? 0 : maxValue;
    const rangeMax = rangeMaxRef.current;
    let yMax = rangeMax;

    const shouldRecalculate = 
      (rangeMax === 0 && safeMaxValue > 0) || 
      (safeMaxValue > rangeMax) || 
      (countRef.current > 10);

    if (shouldRecalculate) {
      countRef.current = 1;
      yMax = 1.2 * safeMaxValue;
      
      if (yMax < 1) {
        yMax = 1;
      } else if (yMax < 10) {
        yMax = Math.ceil(yMax);
      } else {
        const step = 10 ** Math.floor(Math.log10(yMax));
        yMax = Math.ceil(yMax / step) * step;
      }
    } else if (rangeMax > 0) {
      countRef.current += 1;
    }

    if (yMax !== rangeMax) {
      rangeMaxRef.current = yMax;
      setYRange([0, yMax]);
    }
  }, [usage, stacked, maxValue, bqnId, mode]);

  if (mode === "standalone") {
    return (
      <WrapperBqn>
        <GroupedOrStacked
          tooltipValueFormatter={(value) =>
            `${formats.twoDecimals(value)} Mbps`
          }
          valueFormat={formats.twoDecimals}
          items={throughputByAspects(filteredUsage, getAspects())}
          showRoundCorners={false}
          showColors={false}
          valuePadding={30}
          key={`throughput${bqnId}`}
          barHeight={39}
          stacked={stacked}
          setStacked={setStacked}
          fixedYRange={yRange || [0, maxValue]}
          margin={CHART_MARGIN}
        />
        <div className="centered">Mbps</div>
      </WrapperBqn>
    );
  }

  return (
    <ClusterUsage
      usagePerBqn={usagePerBqn}
      stacked={stacked}
      setStacked={setStacked}
      fixedYRange={yRange || [0, maxValue]}
    />
  );
};

export default Result;
